import React, { useState, Fragment } from "react";
import { Dialog, Transition } from "@headlessui/react";
import { X, Shield, ShieldAlert, ShieldCheck, AlertTriangle, Info, ChevronDown, ChevronRight } from "lucide-react";
import type { ScanIssue } from "../../types";

export interface ScannedFileResult {
  fileName: string;
  scanStatus: "clean" | "flagged" | "blocked" | "not_scanned" | "pending" | "failed";
  scanIssues: ScanIssue[];
  scanId?: string;
isGoogleDrive?: boolean;
  googleDriveId?: string;
  isDropbox?: boolean;
  dropboxFileId?: string;
  fileHash?: string;
  fileSize?: number;
}

interface BulkSecurityReportModalProps {
  isOpen: boolean;
  onClose: () => void;
  onUpload: () => void;
  results: ScannedFileResult[];
}

const SEVERITY_CONFIG = {
  HIGH: {
    color: "#ef4444",
    bg: "#fef2f2",
    border: "#fecaca",
    label: "High",
    icon: ShieldAlert,
  },
  MEDIUM: {
    color: "#f59e0b",
    bg: "#fffbeb",
    border: "#fde68a",
    label: "Medium",
    icon: AlertTriangle,
  },
  LOW: {
    color: "#3b82f6",
    bg: "#eff6ff",
    border: "#bfdbfe",
    label: "Low",
    icon: Info,
  },
};

const STATUS_CONFIG = {
  clean: {
    color: "#22c55e",
    bg: "#f0fdf4",
    border: "#bbf7d0",
    label: "Clean",
    icon: ShieldCheck,
  },
  flagged: {
    color: "#f59e0b",
    bg: "#fffbeb",
    border: "#fde68a",
    label: "Flagged",
    icon: Shield,
  },
  blocked: {
    color: "#ef4444",
    bg: "#fef2f2",
    border: "#fecaca",
    label: "Blocked",
    icon: ShieldAlert,
  },
  not_scanned: {
    color: "#6b7280",
    bg: "#f3f4f6",
    border: "#e5e7eb",
    label: "Not Scanned",
    icon: Shield,
  },
  pending: {
    color: "#3b82f6",
    bg: "#eff6ff",
    border: "#bfdbfe",
    label: "Scan Pending",
    icon: Info,
  },
  failed: {
    color: "#ef4444",
    bg: "#fef2f2",
    border: "#fecaca",
    label: "Scan Failed",
    icon: ShieldAlert,
  },
};

const getFriendlyLabel = (type: string) => {
  const map: Record<string, string> = {
    'CREDIT_DEBIT_CARD_NUMBER': 'Credit Card',
    'CREDIT_DEBIT_CARD_EXPIRY': 'Credit Card Expiry',
    'CREDIT_DEBIT_CARD_CVV': 'Credit Card CVV',
    'EMAIL_ADDRESS': 'Email Address',
    'PHONE_NUMBER': 'Phone Number',
    'PASSWORD': 'Password',
    'AWS_ACCESS_KEY': 'AWS Access Key',
    'AWS_SECRET_KEY': 'AWS Secret Key'
  };
  return map[type] || type;
};

const FileReportItem: React.FC<{ result: ScannedFileResult; defaultOpen: boolean }> = ({ result, defaultOpen }) => {
  const [isOpen, setIsOpen] = useState(defaultOpen);
  const statusInfo = STATUS_CONFIG[result.scanStatus] || STATUS_CONFIG.clean;
  const StatusIcon = statusInfo.icon;

  const groupedIssues: Record<string, ScanIssue[]> = { HIGH: [], MEDIUM: [], LOW: [] };
  (result.scanIssues || []).forEach((issue) => {
    const sev = issue.severity || "MEDIUM";
    if (!groupedIssues[sev]) groupedIssues[sev] = [];
    groupedIssues[sev].push(issue);
  });

  const severityOrder: ("HIGH" | "MEDIUM" | "LOW")[] = ["HIGH", "MEDIUM", "LOW"];

  return (
    <div style={{ border: "1px solid #e5e7eb", borderRadius: "8px", marginBottom: "12px", overflow: "hidden" }}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        style={{
          width: "100%",
          padding: "12px 16px",
          display: "flex",
          alignItems: "center",
          backgroundColor: "#f9fafb",
          border: "none",
          borderBottom: isOpen ? "1px solid #e5e7eb" : "none",
          cursor: "pointer",
        }}
      >
        <div style={{ flex: 1, display: "flex", alignItems: "center", gap: "10px", textAlign: "left" }}>
          <StatusIcon size={18} color={statusInfo.color} />
          <div>
            <div style={{ fontSize: "14px", fontWeight: 600, color: "#111827", wordBreak: "break-all" }}>
              {result.fileName}
            </div>
            <div style={{ fontSize: "12px", color: statusInfo.color, fontWeight: 500 }}>
              {statusInfo.label} {result.scanIssues.length > 0 && `(${result.scanIssues.length} findings)`}
            </div>
          </div>
        </div>
        {isOpen ? <ChevronDown size={18} color="#6b7280" /> : <ChevronRight size={18} color="#6b7280" />}
      </button>

      {isOpen && (
        <div style={{ padding: "16px", backgroundColor: "#ffffff" }}>
          {result.scanIssues.length === 0 ? (
            <div style={{ fontSize: "13px", color: "#6b7280", textAlign: "center", padding: "12px 0" }}>
              {result.scanStatus === 'not_scanned' ? 'This file was not scanned.' : 'No issues found.'}
            </div>
          ) : (
            <div style={{ display: "flex", flexDirection: "column", gap: "16px" }}>
              {severityOrder.map((sev) => {
                const issues = groupedIssues[sev];
                if (!issues || issues.length === 0) return null;
                const sevConfig = SEVERITY_CONFIG[sev];

                return (
                  <div key={sev}>
                    <div style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "8px",
                      marginBottom: "12px",
                      color: sevConfig.color,
                      fontSize: "12px",
                      fontWeight: 700,
                    }}>
                      <sevConfig.icon size={16} />
                      {sev} SEVERITY ({issues.length})
                    </div>

                    <div style={{ display: "flex", flexDirection: "column", gap: "6px" }}>
                      {issues.map((issue, idx) => (
                        <div
                          key={idx}
                          style={{
                            padding: "8px",
                            borderRadius: "6px",
                            border: `1px solid ${sevConfig.border}`,
                            backgroundColor: sevConfig.bg,
                          }}
                        >
                          <div style={{ display: "flex", alignItems: "center", gap: "6px", marginBottom: "8px" }}>
                            <span style={{ fontSize: "14px", fontWeight: 700, color: "#111827" }}>
                              {getFriendlyLabel(issue.label || issue.type)}
                            </span>
                            <span style={{
                              padding: "2px 8px",
                              borderRadius: "4px",
                              backgroundColor: issue.action === "BLOCK" ? "#ef4444" : "#3b82f6",
                              color: "white",
                              fontSize: "11px",
                              fontWeight: 700,
                              textTransform: "uppercase"
                            }}>
                              {issue.action || "BLOCKED"}
                            </span>
                          </div>

                          <div style={{
                            padding: "6px 8px",
                            borderRadius: "4px",
                            border: "1px solid #e2e8f0",
                            backgroundColor: "#ffffff",
                            fontFamily: "ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace",
                            fontSize: "13px",
                            color: "#334155",
                            marginBottom: "8px"
                          }}>
                            {issue.redacted_value || "****"}
                          </div>

                          <div style={{ display: "flex", gap: "6px", fontSize: "11px", color: "#475569" }}>
                            <span style={{ backgroundColor: "#f3f4f6", padding: "3px 6px", borderRadius: "4px" }}>
                              Source: {issue.source || "BEDROCK"}
                            </span>
                            <span style={{ backgroundColor: "#f1f5f9", padding: "3px 6px", borderRadius: "4px" }}>
                              Type: {issue.type || "PII"}
                            </span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

const BulkSecurityReportModal: React.FC<BulkSecurityReportModalProps> = ({
  isOpen,
  onClose,
  onUpload,
  results,
}) => {

  const hasBlocked = results.some(r => r.scanStatus === "blocked");
  const hasFlagged = results.some(r => r.scanStatus === "flagged");

  const totalIssues = results.reduce((sum, r) => sum + (r.scanIssues?.length || 0), 0);

  return (
    <Transition appear show={isOpen} as={Fragment}>
      <Dialog as="div" className="relative z-[9999]" onClose={onClose}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black/50" />
        </Transition.Child>

        <div className="fixed inset-0 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4 text-center">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <Dialog.Panel
                className="relative w-full max-w-[460px] mx-4 overflow-hidden text-left align-middle transition-all transform"
                style={{
                  backgroundColor: "#ffffff",
                  borderRadius: "16px",
                  boxShadow: "0 25px 50px -12px rgba(0, 0, 0, 0.25)",
                  maxHeight: "75vh",
                  display: "flex",
                  flexDirection: "column",
                }}
              >
        {/* Header */}
        <div
          style={{
            padding: "20px 24px",
            borderBottom: "1px solid #e5e7eb",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: "16px" }}>
            <div
              style={{
                width: "40px",
                height: "40px",
                borderRadius: "10px",
                backgroundColor: hasBlocked ? "#fef2f2" : hasFlagged ? "#fffbeb" : "#f0fdf4",
                border: `2px solid ${hasBlocked ? "#fecaca" : hasFlagged ? "#fde68a" : "#bbf7d0"}`,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              {hasBlocked ? (
                <ShieldAlert size={20} color="#ef4444" />
              ) : hasFlagged ? (
                <Shield size={20} color="#f59e0b" />
              ) : (
                <ShieldCheck size={20} color="#22c55e" />
              )}
            </div>
            <div>
              <h2 style={{ margin: 0, fontSize: "18px", fontWeight: 700, color: "#111827" }}>
                Security Scan Report
              </h2>
              <p style={{ margin: 0, fontSize: "13px", color: "#6b7280" }}>
                {results.length} file{results.length !== 1 ? 's' : ''} scanned • {totalIssues} finding{totalIssues !== 1 ? 's' : ''}
              </p>
            </div>
          </div>
          <button
            onClick={(e) => {
              e.stopPropagation();
              onClose();
            }}
            style={{
              background: "none",
              border: "none",
              cursor: "pointer",
              padding: "6px",
              borderRadius: "8px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
            onMouseOver={(e) => (e.currentTarget.style.backgroundColor = "#f3f4f6")}
            onMouseOut={(e) => (e.currentTarget.style.backgroundColor = "transparent")}
          >
            <X size={18} color="#6b7280" />
          </button>
        </div>

        {/* Content */}
        <div style={{ padding: "20px 24px", overflowY: "auto", flex: 1 }}>
          {results.map((result, idx) => (
            <FileReportItem key={idx} result={result} defaultOpen={result.scanStatus === "blocked" || result.scanStatus === "flagged" || results.length === 1} />
          ))}
        </div>

        {/* Footer Actions */}
        <div style={{
          padding: "16px 24px",
          borderTop: "1px solid #e5e7eb",
          backgroundColor: "#f9fafb",
          display: "flex",
          justifyContent: "flex-end",
          gap: "12px",
        }}>
          <button
            onClick={onClose}
            style={{
              padding: "8px 16px",
              borderRadius: "8px",
              border: "1px solid #d1d5db",
              backgroundColor: "#ffffff",
              color: "#374151",
              fontSize: "14px",
              fontWeight: 600,
              cursor: "pointer",
            }}
          >
            Cancel
          </button>

          <button
            onClick={onUpload}
            style={{
              padding: "8px 16px",
              borderRadius: "8px",
              border: "none",
              backgroundColor: (hasFlagged || hasBlocked) ? "#f59e0b" : "#3b82f6",
              color: "#ffffff",
              fontSize: "14px",
              fontWeight: 600,
              cursor: "pointer",
            }}
          >
            {(hasFlagged || hasBlocked) ? "Upload Anyway" : "Upload"}
          </button>
        </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
};

export default BulkSecurityReportModal;
